'use client';

import { c as _c } from "react/compiler-runtime";
import * as Sentry from '@sentry/nextjs';
import { useReportWebVitals } from 'next/web-vitals';
import { useEffect } from 'react';
import { isBot } from '@/utils/isBot';
import { getLargestShiftTarget, startLayoutShiftTracking } from '@/utils/layoutShiftTarget';
import { describeElement } from '@/utils/webVitalTarget';

// LCP 엔트리는 PerformanceEntry에 LCP 요소·리소스 URL을 얹어 제공한다(web-vitals가 metric.entries로 전달)
interface LargestContentfulPaintEntry extends PerformanceEntry {
  element?: Element | null;
  url?: string;
}
const CLS_NEEDS_IMPROVEMENT_THRESHOLD = 0.1;
const CLS_GUARD_SAMPLE = 0.1;
const LCP_NEEDS_IMPROVEMENT_THRESHOLD = 2500;
const DEFAULT_LCP_GUARD_SAMPLE = 0.1;
function parseLcpGuardSample(): number {
  const raw = process.env.NEXT_PUBLIC_LCP_GUARD_SAMPLE;
  if (raw === undefined || raw === null) {
    return DEFAULT_LCP_GUARD_SAMPLE;
  }
  const parsed = parseFloat(raw);
  if (isNaN(parsed)) {
    return DEFAULT_LCP_GUARD_SAMPLE;
  }
  return parsed;
}
export const WebVitalsReporter = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
  useReportWebVitals(_temp2);
  useReportWebVitals(_temp3);
  return null;
};
function _temp() {
  startLayoutShiftTracking();
}
function _temp2(metric) {
  if (metric.name !== "CLS" || metric.value < CLS_NEEDS_IMPROVEMENT_THRESHOLD || isBot()) {
    return;
  }
  if (Math.random() >= CLS_GUARD_SAMPLE) {
    return;
  }
  Sentry.captureMessage(`CLS 임계 초과: ${metric.value.toFixed(3)}`, {
    level: "warning",
    tags: {
      webVital: "CLS",
      route: window.location.pathname,
      clsTarget: getLargestShiftTarget() || "unknown"
    },
    extra: {
      value: metric.value,
      metricId: metric.id
    }
  });
}
function _temp3(metric_0) {
  if (metric_0.name !== "LCP") {
    return;
  }
  if (metric_0.value < LCP_NEEDS_IMPROVEMENT_THRESHOLD) {
    return;
  }
  if (isBot()) {
    return;
  }
  const sampleRate = parseLcpGuardSample();
  if (sampleRate === 0) {
    return;
  }
  if (Math.random() >= sampleRate) {
    return;
  }
  const lcpEntry = metric_0.entries.at(-1) as LargestContentfulPaintEntry | undefined;
  Sentry.captureMessage(`LCP 임계 초과: ${(metric_0.value / 1000).toFixed(2)}s`, {
    level: "warning",
    tags: {
      webVital: "LCP",
      route: window.location.pathname,
      lcpTarget: describeElement(lcpEntry?.element) || "unknown"
    },
    extra: {
      value: metric_0.value,
      metricId: metric_0.id,
      lcpUrl: lcpEntry?.url || undefined
    }
  });
}